// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { AuthService } from '../login/services/auth.service';
import { SystemMessage, DocumentQueueUpdate } from '@interfaces/document.interfaces';
import { catchError, from, map, Observable } from 'rxjs';

const Events = {
    DocumentQueueUpdated: 'DocumentQueueUpdated',
};

@Injectable({ providedIn: 'root' })
export class EventsService {
    private readonly apiUri = environment.apiUrlV2 + 'api/hubs/events';
    private hub: HubConnection = null;
    private _connected: Observable<boolean> = null;

    constructor(private readonly authService: AuthService) {}

    public build(): Promise<void> {
        return new Promise((complete) => {
            if (null !== this.hub) {
                // no need to build (but possibly still connecting)
                this._connected?.subscribe(() => complete());
                return;
            }

            const options: IHttpConnectionOptions = {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
                accessTokenFactory: () => this.authService.token,
            };

            this.hub = new HubConnectionBuilder().configureLogging(LogLevel.Information).withUrl(this.apiUri, options).build();
            this._connected = from(this.hub.start()).pipe(
                catchError((err) => {
                    console.error('Error while establishing connection', err);
                    return null;
                }),
                map(() => {
                    return true;
                })
            );
            this._connected.subscribe(() => complete());
        });
    }

    public onSystemMessages(handler: (SystemMessage) => void): void {
        this.hub.on('ActiveSystemMessages', (messages) => {
            handler(messages);
        });
    }

    public onDocumentQueueUpdate(handler: (update: DocumentQueueUpdate) => void): void {
        this.hub.on(Events.DocumentQueueUpdated, (update) => {
            handler(update);
        });
    }

    public offDocumentQueueUpdate(): void {
        this.hub.off(Events.DocumentQueueUpdated);
    }

    public subscribeToDeal(dealId: number): Promise<void> {
        return this.hub.invoke('subscribeToDeal', dealId);
    }

    public unsubscribeFromDeal(dealId: number): Promise<void> {
        return this.hub.invoke('unsubscribeFromDeal', dealId);
    }
}
